/**
 * PlaybookCreationStepper (DEV-1266, U2) — turn a completed mini-project
 * into a reusable Playbook via clone-as-master.
 *
 * Four steps: Basics → Sharing → Collection → Review. On Create, calls
 * POST /api/mini-project-playbooks/from-mini-project/{guid}; on success
 * the parent navigates straight into the editable master (decision #14).
 *
 * Anti-derivation responses are surfaced gracefully:
 *   • 409 — a playbook already exists from this project / same-community
 *           source → offer to open the existing master.
 *   • 403 — cross-org-family source → explain it can't be templatized.
 *
 * Collection is mandatory (decision #11). First cut: link an existing
 * collection (scoped to the owning community), or create one with a
 * platform schema. Org-custom schema selection arrives with Structures.
 *
 * Mockup: design-mockups/dev-903-completion-playbook-flow.html (Screen 2).
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import {
  createPlaybookFromMiniProject,
  fetchTemplatizeEligibility,
  type MiniProject,
  type MiniProjectPlaybook,
  type TemplatizeEligibility,
} from "../../api/miniProjects";
import {
  fetchAllCollections,
  fetchAllSchemas,
  type CollectionResponse,
  type CollectionSchemaResponse,
} from "../../api/communityMetadata";
import { ApiError } from "../../api/client";
import styles from "./PlaybookCreationStepper.module.css";

interface Props {
  source: MiniProject;
  onClose: () => void;
  /** Called with the created playbook so the parent can navigate to its
   *  master. */
  onCreated: (playbook: MiniProjectPlaybook) => void;
}

type Scope = "private" | "cross_org_visible" | "public_open";
type CollMode = "existing" | "new";

const STEPS = ["basics", "sharing", "collection", "review"] as const;

export default function PlaybookCreationStepper({ source, onClose, onCreated }: Props) {
  const { t } = useAppTranslation("pages");
  const ownerCommunityGuid = source.ownerCommunityGuid;

  const [step, setStep] = useState(0);
  const [title, setTitle] = useState(source.title || "");
  const [description, setDescription] = useState(source.description || source.shortDescription || "");
  const [categoryKey, setCategoryKey] = useState(source.categoryKey || "");
  const [tagsText, setTagsText] = useState("");
  const [scope, setScope] = useState<Scope>("private");

  const [collMode, setCollMode] = useState<CollMode>("existing");
  const [collectionGuid, setCollectionGuid] = useState("");
  const [collectionName, setCollectionName] = useState("");
  const [collectionSchemaGuid, setCollectionSchemaGuid] = useState("");

  const [collections, setCollections] = useState<CollectionResponse[]>([]);
  const [platformSchemas, setPlatformSchemas] = useState<CollectionSchemaResponse[]>([]);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set when the backend says a playbook already exists (409) — carries
  // the existing master so we can offer "Open master".
  const [existingMasterGuid, setExistingMasterGuid] = useState<string | null>(null);

  // DEV-1266: pre-flight eligibility, so the user learns up front (not after
  // filling the whole form). null = still checking.
  const [eligibility, setEligibility] = useState<TemplatizeEligibility | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetchTemplatizeEligibility(source.miniProjectGuid)
      .then((r) => { if (!cancelled) setEligibility(r); })
      .catch(() => { if (!cancelled) setEligibility({ eligible: true, reason: "ok" }); });
    return () => { cancelled = true; };
  }, [source.miniProjectGuid]);

  /* ── Load collections + platform schemas ── */
  useEffect(() => {
    let cancelled = false;
    fetchAllCollections(ownerCommunityGuid)
      .then((rows) => { if (!cancelled) setCollections(rows.filter((c) => c.isActive)); })
      .catch(() => { if (!cancelled) setCollections([]); });
    fetchAllSchemas()
      .then((rows) => { if (!cancelled) setPlatformSchemas(rows.filter((s) => s.isPlatform)); })
      .catch(() => { if (!cancelled) setPlatformSchemas([]); });
    return () => { cancelled = true; };
  }, [ownerCommunityGuid]);

  // Default the new-collection name to the playbook title once known.
  useEffect(() => {
    if (collMode === "new" && !collectionName && title) {
      setCollectionName(t("playbookStepper.collectionNameDefault", { title }));
    }
  }, [collMode, collectionName, title, t]);

  /* ── Per-step validity ── */
  const stepValid = useMemo(() => {
    switch (STEPS[step]) {
      case "basics":
        return title.trim().length > 0;
      case "sharing":
        return true;
      case "collection":
        return collMode === "existing"
          ? collectionGuid.length > 0
          : collectionName.trim().length > 0 && collectionSchemaGuid.length > 0;
      default:
        return true;
    }
  }, [step, title, collMode, collectionGuid, collectionName, collectionSchemaGuid]);

  const tags = useMemo(
    () => tagsText.split(",").map((s) => s.trim()).filter(Boolean),
    [tagsText],
  );

  /* ── Submit ── */
  const handleCreate = useCallback(async () => {
    setBusy(true);
    setError(null);
    setExistingMasterGuid(null);
    try {
      const playbook = await createPlaybookFromMiniProject(source.miniProjectGuid, {
        ownerCommunityGuid,
        title: title.trim(),
        description: description.trim() || undefined,
        categoryKey: categoryKey.trim() || undefined,
        tags,
        scope,
        ...(collMode === "existing"
          ? { collectionGuid }
          : { collectionName: collectionName.trim(), collectionSchemaGuid }),
      });
      onCreated(playbook);
    } catch (e) {
      if (e instanceof ApiError) {
        const body = (e.body || {}) as { error?: string; masterMiniProjectGuid?: string };
        if (e.status === 409 && body.masterMiniProjectGuid) {
          setExistingMasterGuid(body.masterMiniProjectGuid);
        }
        setError(body.error || e.message);
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
      setBusy(false);
    }
  }, [source.miniProjectGuid, ownerCommunityGuid, title, description, categoryKey, tags, scope, collMode, collectionGuid, collectionName, collectionSchemaGuid, onCreated]);

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));
  const isLast = step === STEPS.length - 1;

  /* ── Step bodies ── */
  const renderStep = () => {
    switch (STEPS[step]) {
      case "basics":
        return (
          <>
            <label className={styles.fl}>{t("playbookStepper.nameLabel")}</label>
            <input className={styles.input} value={title} onChange={(e) => setTitle(e.target.value)} />
            <div className={styles.hint}>{t("playbookStepper.nameHint")}</div>
            <label className={styles.fl}>{t("playbookStepper.descLabel")}</label>
            <textarea className={styles.textarea} value={description} onChange={(e) => setDescription(e.target.value)} />
            <label className={styles.fl}>{t("playbookStepper.categoryLabel")}</label>
            <input className={styles.input} value={categoryKey} onChange={(e) => setCategoryKey(e.target.value)} />
            <label className={styles.fl}>{t("playbookStepper.tagsLabel")}</label>
            <input className={styles.input} value={tagsText} onChange={(e) => setTagsText(e.target.value)}
              placeholder={t("playbookStepper.tagsPlaceholder")} />
          </>
        );
      case "sharing":
        return (
          <>
            <div className={styles.stepIntro}>{t("playbookStepper.sharingIntro")}</div>
            {([
              ["private", "scopePrivateTitle", "scopePrivateHint"],
              ["cross_org_visible", "scopeCrossTitle", "scopeCrossHint"],
              ["public_open", "scopePublicTitle", "scopePublicHint"],
            ] as const).map(([val, titleKey, hintKey]) => (
              <button
                key={val}
                type="button"
                className={`${styles.choice} ${scope === val ? styles.choiceSel : ""}`}
                onClick={() => setScope(val)}
              >
                <span className={styles.radio} />
                <span>
                  <strong>{t(`playbookStepper.${titleKey}`)}</strong>
                  <span className={styles.choiceHint}>{t(`playbookStepper.${hintKey}`)}</span>
                </span>
              </button>
            ))}
          </>
        );
      case "collection":
        return (
          <>
            <div className={styles.stepIntro}>{t("playbookStepper.collectionIntro")}</div>
            <button
              type="button"
              className={`${styles.choice} ${collMode === "existing" ? styles.choiceSel : ""}`}
              onClick={() => setCollMode("existing")}
            >
              <span className={styles.radio} />
              <span><strong>{t("playbookStepper.collectionExisting")}</strong></span>
            </button>
            {collMode === "existing" && (
              <div className={styles.sub}>
                {collections.length === 0 ? (
                  <div className={styles.hint}>{t("playbookStepper.collectionNone")}</div>
                ) : (
                  <select className={styles.input} value={collectionGuid} onChange={(e) => setCollectionGuid(e.target.value)}>
                    <option value="">{t("playbookStepper.collectionPick")}</option>
                    {collections.map((c) => (
                      <option key={c.collectionGuid} value={c.collectionGuid}>{c.collectionName}</option>
                    ))}
                  </select>
                )}
              </div>
            )}
            <button
              type="button"
              className={`${styles.choice} ${collMode === "new" ? styles.choiceSel : ""}`}
              onClick={() => setCollMode("new")}
            >
              <span className={styles.radio} />
              <span><strong>{t("playbookStepper.collectionNew")}</strong></span>
            </button>
            {collMode === "new" && (
              <div className={styles.sub}>
                <label className={styles.fl}>{t("playbookStepper.collectionNameLabel")}</label>
                <input className={styles.input} value={collectionName} onChange={(e) => setCollectionName(e.target.value)} />
                <label className={styles.fl}>{t("playbookStepper.collectionSchemaLabel")}</label>
                <select className={styles.input} value={collectionSchemaGuid} onChange={(e) => setCollectionSchemaGuid(e.target.value)}>
                  <option value="">{t("playbookStepper.collectionSchemaPick")}</option>
                  {platformSchemas.map((s) => (
                    <option key={s.schemaGuid} value={s.schemaGuid}>{s.schemaName}</option>
                  ))}
                </select>
                <div className={styles.hint}>{t("playbookStepper.collectionSchemaHint")}</div>
              </div>
            )}
          </>
        );
      case "review":
        return (
          <>
            <div className={styles.stepIntro}>{t("playbookStepper.reviewIntro")}</div>
            <div className={styles.reviewRow}><span>{t("playbookStepper.nameLabel")}</span><b>{title}</b></div>
            <div className={styles.reviewRow}><span>{t("playbookStepper.sharingLabel")}</span><b>{t(`playbookStepper.scope_${scope}`)}</b></div>
            <div className={styles.reviewRow}>
              <span>{t("playbookStepper.collectionLabel")}</span>
              <b>{collMode === "existing"
                ? (collections.find((c) => c.collectionGuid === collectionGuid)?.collectionName || "—")
                : collectionName}</b>
            </div>
            <div className={styles.note}>{t("playbookStepper.reviewNote")}</div>
          </>
        );
      default:
        return null;
    }
  };

  // DEV-1266: gate — checking, or ineligible up front, before the form.
  const blocked = eligibility != null && !eligibility.eligible;
  const gateMaster = eligibility?.existingMasterGuid || null;

  const gateBody = () => {
    if (eligibility == null) {
      return <div className={styles.gateMsg}>{t("playbookStepper.eligChecking")}</div>;
    }
    const key =
      eligibility.reason === "not_complete" ? "eligNotComplete"
      : eligibility.reason === "already_exists" ? "eligAlreadyExists"
      : eligibility.reason === "same_community" ? "eligAlreadyExists"
      : eligibility.reason === "cross_org" ? "eligCrossOrg"
      : eligibility.reason === "is_master" ? "eligIsMaster"
      : "eligGeneric";
    return <div className={styles.gateMsg}>{t(`playbookStepper.${key}`)}</div>;
  };

  const dialog = (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className={styles.head}>
          <h2 className={styles.title}>{t("playbookStepper.title")}</h2>
          <button className={styles.x} onClick={onClose} aria-label={t("miniProject.completion.close")}>✕</button>
        </div>

        {/* Eligibility gate — checking or ineligible: show the reason, not the form. */}
        {(eligibility == null || blocked) ? (
          <>
            <div className={styles.body}>{gateBody()}</div>
            <div className={styles.foot}>
              <span className={styles.spacer} />
              {gateMaster && (
                <button className={styles.btnGhost} onClick={() => onCreated({ masterMiniProjectGuid: gateMaster } as MiniProjectPlaybook)}>
                  {t("playbookStepper.openExistingMaster")}
                </button>
              )}
              <button className={styles.btnPrimary} onClick={onClose}>
                {t("miniProject.completion.done")}
              </button>
            </div>
          </>
        ) : (
        <>
        {/* Step rail */}
        <div className={styles.steps}>
          {STEPS.map((s, i) => (
            <div key={s} className={`${styles.stepChip} ${i === step ? styles.stepNow : i < step ? styles.stepDone : ""}`}>
              <span className={styles.stepNum}>{i + 1}</span>
              {t(`playbookStepper.step_${s}`)}
            </div>
          ))}
        </div>

        <div className={styles.body}>
          {renderStep()}
          {error && (
            <div className={styles.error}>
              {error}
              {existingMasterGuid && (
                <button className={styles.errorAction} onClick={() => onCreated({ masterMiniProjectGuid: existingMasterGuid } as MiniProjectPlaybook)}>
                  {t("playbookStepper.openExistingMaster")}
                </button>
              )}
            </div>
          )}
        </div>

        <div className={styles.foot}>
          <button className={styles.btnGhost} onClick={back} disabled={step === 0 || busy}>
            {t("playbookStepper.back")}
          </button>
          <span className={styles.spacer} />
          <span className={styles.stepCount}>{t("playbookStepper.stepCount", { n: step + 1, total: STEPS.length })}</span>
          {isLast ? (
            <button className={styles.btnPrimary} onClick={handleCreate} disabled={busy || !stepValid}>
              {busy ? t("playbookStepper.creating") : t("playbookStepper.create")}
            </button>
          ) : (
            <button className={styles.btnPrimary} onClick={next} disabled={!stepValid}>
              {t("playbookStepper.continue")}
            </button>
          )}
        </div>
        </>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(dialog, document.body);
}
